import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FileUploadService } from '../../services/file-upload.service';
import { LayoutService } from '../../services/layout.service';
import { AuthService } from '../../services/auth.service';
import { Subscription } from 'rxjs';

interface NavigationItem {
  label: string;
  route?: string;
  icon: string;
  iconActive?: string;
  action?: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatSnackBarModule
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  isSidebarMode = false;
  isAuthenticated = false;
  private sidebarModeSubscription?: Subscription;
  private authSubscription?: Subscription;
  showNewMenu = false;

  navigationItems: NavigationItem[] = [
    { label: 'Home', route: '/dashboard', icon: 'home-dark-grey', iconActive: 'home-blue' },
    { label: 'separator', route: undefined, icon: '' },
    { label: 'New', route: undefined, icon: 'add-dark-grey', iconActive: 'add-blue', action: 'new' },
    { label: 'My Data', route: '/data-sources', icon: 'data-set-dark-grey', iconActive: 'data-set-blue' },
    { label: 'Activity', route: '/activity', icon: 'docs-dark-grey', iconActive: 'docs-blue' }
  ];

  constructor(
    private fileUploadService: FileUploadService,
    private router: Router,
    private snackBar: MatSnackBar,
    private layoutService: LayoutService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.sidebarModeSubscription = this.layoutService.sidebarMode$.subscribe(
      isActive => this.isSidebarMode = isActive
    );
    
    this.authSubscription = this.authService.currentUser$.subscribe(
      user => this.isAuthenticated = !!user
    );
  }

  ngOnDestroy(): void {
    this.sidebarModeSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
  }

  async onItemClick(item: NavigationItem): Promise<void> {
    // Prevent navigation when not authenticated
    if (!this.isAuthenticated) {
      return;
    }
    
    if (item.action === 'upload') {
      await this.handleFileUpload();
    } else if (item.action === 'new') {
      this.showNewMenu = !this.showNewMenu;
    } else if (item.label === 'Home' && this.router.url === '/dashboard') {
      // If already on dashboard/homepage, trigger reset
      this.layoutService.triggerDashboardReset();
    }
    // For regular routes, the routerLink in template will handle navigation
  }

  onNewMenuItemClick(action: 'new-datasource' | 'profile-data' | 'anomaly-detection' | 'ai-readiness' | 'clean-data' | 'hypothesis-testing'): void {
    this.showNewMenu = false;
    this.router.navigate(['/dashboard'], {
      queryParams: { action }
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.nav-item') && !target.closest('.new-menu-dropdown')) {
      this.showNewMenu = false;
    }
  }

  private async handleFileUpload(): Promise<void> {
    try {
      // Show loading message
      this.snackBar.open('Please select a JSON or CSV file...', '', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });

      // Open file dialog
      const uploadedData = await this.fileUploadService.openFileDialog(['.json', '.csv']);
      
      if (uploadedData) {
        // Show success message
        this.snackBar.open(
          `Successfully loaded ${uploadedData.fileName} (${uploadedData.rowCount} rows)`, 
          '×', 
          {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          }
        );

        // Navigate to data profile with ID 0 (representing uploaded data)
        this.router.navigate(['/data/profile/0']);
      }
    } catch (error) {
      // Show error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to process file';
      this.snackBar.open(
        `Error: ${errorMessage}`, 
        '×', 
        {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'error-snackbar'
        }
      );
    }
  }
}
